import { FC, useState } from 'react';

import { ArrowIcon } from '../../../assets/icons/index';
import { AppFormattedMessage } from '../../../components/app-formatted-message/index';
import Button from '../../../components/button/index';
import { PaginationController } from '../../../components/pagination-controller/index';
import { useFakePagination, useLocale } from '../../../hooks/index';
import { PublicCapTablePayload } from '../../../hooks/shared-cap-table/shared-cap-table.types';
import { stakeholderTypeTranslation } from '../../../translations/stakeholder-translation.util';
import { DetailsSortVariant, detailsSortVariantTitleMap } from '../../../types/snapshot.types';
import { StakeholderType } from '../../../types/stakeholder.types';
import { PublicDetailsItem, PublicDetailsMetrics, PublicDetailsRow } from './public-details-item';

type PublicCapTableMobileProps = {
  /** Lets the page drop the blocks the drilled-in designs do not carry. */
  onDrilledChange: (isDrilled: boolean) => void;
  onViewChange: (view: DetailsSortVariant) => void;
  payload: PublicCapTablePayload;
  selectedView: DetailsSortVariant;
};

enum MobileLevel {
  CATEGORY = 'category',
  DETAILS = 'details',
  POSITIONS = 'positions',
}

const ROWS_PER_PAGE = 13;

const EMPTY_ROW: PublicDetailsRow = {
  converted: 0,
  convertedPercentage: 0,
  diluted: 0,
  dilutedPercentage: 0,
  investment: 0,
  issued: 0,
  issuedPercentage: 0,
  votingPercentage: 0,
  votingRight: 0,
};

/**
 * A row is present when it holds diluted shares — the same test the desktop renderer applies, and
 * the only one available here: `issued` is zeroed unless the link shares that column, so filtering
 * on it would empty the list for a link that withheld it.
 */
const holdsShares = ({ diluted }: { diluted: number }) => diluted > 0;

/**
 * The desktop table put in one screen what a phone has to walk through, so the mobile design splits
 * it the way the app's own cap table does: totals, then positions, then the holders behind a
 * category. Steps are state rather than routes — the link resolves to exactly one URL, and pushing
 * sub-paths onto it would invite a viewer to share or bookmark a URL that does not exist.
 */
export const PublicCapTableMobile: FC<PublicCapTableMobileProps> = ({
  onDrilledChange,
  onViewChange,
  payload,
  selectedView,
}) => {
  const { messagesLocale } = useLocale();
  const [level, setLevel] = useState(MobileLevel.DETAILS);
  const [selectedCategory, setSelectedCategory] = useState<null | StakeholderType>(null);
  const [currentPage, setCurrentPage] = useState(1);

  const visibleColumns = payload.columns[selectedView];
  const categories = payload.stakeholdersTypes.filter(holdsShares);
  const stakeholders = payload.stakeholders.filter(holdsShares);

  const categoryTitle = (type: StakeholderType) => messagesLocale[stakeholderTypeTranslation[type]];

  const totals = categories.reduce<PublicDetailsRow>(
    (acc, category) => ({
      ...acc,
      diluted: acc.diluted + category.diluted,
      dilutedPercentage: acc.dilutedPercentage + category.dilutedPercentage,
      investment: acc.investment + category.invested,
      issued: acc.issued + category.issued,
      issuedPercentage: acc.issuedPercentage + category.issuedPercentage,
      votingPercentage: acc.votingPercentage + category.votingPercentage,
    }),
    EMPTY_ROW,
  );

  const openLevel = (next: MobileLevel, category: null | StakeholderType = null) => {
    setSelectedCategory(category);
    setCurrentPage(1);
    setLevel(next);
    onDrilledChange(next !== MobileLevel.DETAILS);
    // Steps are state, not routes, so the browser keeps the scroll position of the screen just
    // left — deep in the list, the next screen opens on its footer instead of its heading.
    window.scrollTo({ top: 0 });
  };

  const categoryStakeholders = stakeholders.filter(({ type }) => type === selectedCategory);
  const listed = level === MobileLevel.CATEGORY ? categoryStakeholders : stakeholders;
  const { data: pagedStakeholders, totalPages } = useFakePagination({
    currentPage,
    data: listed,
    take: ROWS_PER_PAGE,
  });

  if (level === MobileLevel.DETAILS) {
    return (
      <div className="flex flex-col overflow-hidden rounded-md shadow-sm">
        <PublicDetailsMetrics row={totals} visibleColumns={visibleColumns} />
        <Button
          className="flex w-full items-center justify-between rounded-none bg-brand-25 px-4 py-3"
          onClick={() => openLevel(MobileLevel.POSITIONS)}
          styleType="NONE"
        >
          <span className="text-sm font-[450] text-brand-700">
            <AppFormattedMessage id="VIEW_POSITIONS" />
          </span>
          <ArrowIcon className="rotate-180" iconColor="#2565C8" />
        </Button>
      </div>
    );
  }

  const isCategoryLevel = level === MobileLevel.CATEGORY;

  return (
    <div className="flex w-full flex-col gap-4">
      <div className="flex gap-2 text-xs font-[450] text-gray-400">
        <button
          className="text-brand-700"
          onClick={() => openLevel(MobileLevel.DETAILS)}
          type="button"
        >
          <AppFormattedMessage id="CAP_TABLE" />
        </button>
        <span>/</span>
        {isCategoryLevel ? (
          <>
            <button
              className="text-brand-700"
              onClick={() => openLevel(MobileLevel.POSITIONS)}
              type="button"
            >
              <AppFormattedMessage id="POSITIONS" />
            </button>
            <span>/</span>
            <span>{selectedCategory && categoryTitle(selectedCategory)}</span>
          </>
        ) : (
          <span>
            <AppFormattedMessage id="POSITIONS" />
          </span>
        )}
      </div>

      <div className="flex items-center gap-2 border-y border-gray-100 py-3">
        <Button
          className="centered size-9 rounded-full border border-gray-100 shadow-sm"
          onClick={() => openLevel(isCategoryLevel ? MobileLevel.POSITIONS : MobileLevel.DETAILS)}
          styleType="NONE"
        >
          <ArrowIcon />
        </Button>
        <div className="flex flex-col">
          <span className="text-base font-semibold text-gray-700">
            {isCategoryLevel && selectedCategory ? (
              categoryTitle(selectedCategory)
            ) : (
              <AppFormattedMessage id="CAP_TABLE" />
            )}
          </span>
          <span className="text-xs font-[450] text-gray-400">
            <AppFormattedMessage id="DETAILS" />
          </span>
        </div>
      </div>

      {!isCategoryLevel && (
        <div className="flex items-center gap-1 rounded-3xl border border-gray-200 p-1">
          {detailsSortVariantTitleMap.map(([viewKey, viewTitle]) => (
            <Button
              className={`h-8 flex-1 rounded-3xl text-xs font-[450] ${
                selectedView === viewKey
                  ? 'bg-brand-700 font-[550] text-white'
                  : 'bg-transparent text-gray-700'
              }`}
              key={viewKey}
              onClick={() => {
                onViewChange(viewKey);
                setCurrentPage(1);
              }}
              styleType="NONE"
            >
              <span className="text-nowrap">
                <AppFormattedMessage id={viewTitle} />
              </span>
            </Button>
          ))}
        </div>
      )}

      {(isCategoryLevel || selectedView === DetailsSortVariant.BY_STAKEHOLDER) && (
        <>
          {pagedStakeholders.map(({ id, invested, name, shareClasses, type, ...stats }) => (
            <PublicDetailsItem
              avatar
              description={categoryTitle(type)}
              key={id}
              name={name}
              row={{ ...stats, investment: invested }}
              shareClasses={shareClasses}
              visibleColumns={visibleColumns}
            />
          ))}
          {totalPages > 1 && (
            <PaginationController
              currentPage={currentPage}
              onClick={setCurrentPage}
              totalPages={totalPages}
            />
          )}
        </>
      )}

      {!isCategoryLevel &&
        selectedView === DetailsSortVariant.BY_SHARE_CLASS &&
        categories.map(({ invested, shareClasses, type, ...stats }) =>
          shareClasses.map((shareClass) => (
            <PublicDetailsItem
              className="pb-0"
              description={shareClass.name}
              key={`${type}-${shareClass.id}`}
              name={categoryTitle(type)}
              row={{ ...stats, investment: invested }}
              shareClasses={[shareClass]}
              visibleColumns={visibleColumns}
            >
              <Button
                className="flex w-full items-center justify-between rounded-none bg-brand-25 px-4 py-3"
                onClick={() => openLevel(MobileLevel.CATEGORY, type)}
                styleType="NONE"
              >
                <span className="text-sm font-[450] text-brand-700">
                  <AppFormattedMessage
                    id="VIEW_ASSOCIATED_STAKEHOLDERS"
                    values={{
                      stakeholdersCount: stakeholders.filter(
                        (stakeholder) => stakeholder.type === type,
                      ).length,
                    }}
                  />
                </span>
                <ArrowIcon className="rotate-180" iconColor="#2565C8" />
              </Button>
            </PublicDetailsItem>
          )),
        )}
    </div>
  );
};
